/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/dashboard.scss';
import './style/todos.scss';

import React, {
    Fragment, KeyboardEventHandler, MouseEventHandler, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import {
    AwesomeIcon, ChangeFunction, Checkbox, ElementPosition, getSetting, hasHover, Link, Loader, MenuPlacement,
    OptionsMenu, OptionsMenuItem, parseMoney, SelectItemShape, voidFunction
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import Invoice from "../../models/invoiceModel";
import {
    AccessKey, AccountProgress, AccountStats, AccountStatus, CompleteInvoiceFilter, CustomerType, InvoiceFilter,
    InvoiceUploadStats, PagerType, SystemMessageType
} from "../../util/interfaces";
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import {AppContext} from "../misc/appContext";
import envelopeFill from '@iconify/icons-bi/envelope-fill';
import fileTextFilled from '@iconify/icons-ant-design/file-text-filled';
import currencyEuro from '@iconify/icons-bi/currency-euro';
import {getInvoiceOptions, getInvoiceStatusLabels, InvoiceStatusElement} from "../../util/dashboardUtil";
import TrialEndController from "../trialEnd/trialEndController";
import {BolmateButton, BolmateInput, BolmateSelect} from "../controls";
import InvoiceFilterController, {InvoiceFilterRef} from "./filter/invoiceFilterController";
import {hasAdminLogin} from "../../util/adminUtil";
import ImportStatus from "./importStatus";
import optionsHorizontal from '@iconify/icons-mi/options-horizontal';
import {formatSimpleDate, onCopy, renderPlural} from "../../util/utils";
import InfoIcon from "../icon/infoIcon";
import CountryFlag from "../misc/countryFlag";
import {getCountryCode} from "../../util/countryUtil";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import Todo from './todo';
import {COLOR_BLUE_BOLMATE, COLOR_GRAY_8} from "../../util/constants";
import {DotsLoader} from "../controls/loader/dotsLoader";
import DashboardStats from './stats/dashboardStats';
import {DashboardTab} from "./dashboardController";
import FlipCard from "../controls/flipCard/flipCard";
import UploadStatsController from "./uploadStats/uploadStatsController";
import MailMessage from './mailMessage';
import InvoiceRequestController from "../invoice/request/invoiceRequestController";
import Image from "../misc/image";
import {userHasRight} from "../../util/accessUtil";
import useRight from "../misc/useRight";
import {InvoiceOption} from "../../util/types";
import {InvoiceOptionName, SubscriptionModuleName, SubscriptionModuleStatus} from "../../util/enums";
import CopyIcon from "../misc/copyIcon";
import BasicMessage from "../misc/basicMessage";

interface Props {
    onClickInvoice: MouseEventHandler;
    onClickVatSettings: MouseEventHandler;
    onClickDetails: (invoice: Invoice) => MouseEventHandler;
    onClickEdit: (invoice: Invoice) => MouseEventHandler;
    onClickMail: (invoice: Invoice | null) => MouseEventHandler;
    onUploadInvoice: (invoice: Invoice) => MouseEventHandler;
    onClickDownload: (invoice: Invoice | null) => MouseEventHandler;
    onPreviewFile: (invoice: Invoice) => MouseEventHandler;
    onChangePage: (page: number, pagerType: PagerType) => MouseEventHandler;
    onChangeSearch: ChangeFunction<string>;
    onChangeLimit: ChangeFunction<number>;
    onSearch: KeyboardEventHandler;
    onClickMailSettings: MouseEventHandler;
    onClickUpload: MouseEventHandler;
    onClickApiFlow: MouseEventHandler;
    onSelectInvoice: (invoiceId: string) => (checked: boolean, evt: React.MouseEvent) => void;
    onSelectAll: ChangeFunction;
    onClickCredit: (invoice: Invoice) => MouseEventHandler;
    onBulkApplyEmailSequence: MouseEventHandler;
    onOpenFilters: MouseEventHandler;
    onCloseFilters: MouseEventHandler & ((evt: MouseEvent) => void);
    onSubmitFilters: (filters: CompleteInvoiceFilter) => void;
    onClickReverseCharge: (invoice: Invoice) => MouseEventHandler;
    onClickAssignInvoiceNumber: (invoice: Invoice) => MouseEventHandler;
    onCopyInvoiceID: (invoice: Invoice) => MouseEventHandler;
    onDisableLambda: (invoice: Invoice) => MouseEventHandler;
    onDismissImportInfo: MouseEventHandler;
    onReload: MouseEventHandler;
    onCheckTodo: (key: keyof AccountProgress) => ChangeFunction;
    onClickUploadSettings: MouseEventHandler;
    reloadNumOpen: VoidFunction;
    emailsEnabled: boolean;
    filtersOpen: boolean;
    page: number;
    invoices: Invoice[];
    stats: AccountStats;
    loadingInvoices: boolean;
    loadingStats: boolean;
    limit: number;
    allSelected: boolean;
    selectedInvoices: string[];
    filter: InvoiceFilter;
    setupProgress: AccountProgress;
    isImportingHistory: boolean;
    sendingHistory: boolean;
    tab: DashboardTab;
    uploadStats: InvoiceUploadStats;
    loadingUploadStats: boolean;
}

const limitItems: SelectItemShape[] = [
    {label: '10', value: 10},
    {label: '20', value: 20},
    {label: '50', value: 50},
];

export default function Dashboard(props: Props) {
    const context = useContext(AppContext);
    const [canBulkDownload] = useRight(AccessKey.INVOICE_BULK_DOWNLOAD);
    const activeAccountId = context.accountContext.account?.id ?? '';
    const {
        filter, page, filtersOpen, onOpenFilters, onSubmitFilters, onCloseFilters, onClickVatSettings,
        onClickDetails, onClickInvoice, onClickCredit, onClickMail, onClickEdit, onPreviewFile, onClickMailSettings,
        onSearch, selectedInvoices, allSelected, onSelectAll, limit, loadingInvoices, loadingStats, invoices,
        onBulkApplyEmailSequence, onChangeSearch, onChangePage, stats, onClickDownload, onDisableLambda, onReload,
        onClickApiFlow, onSelectInvoice, emailsEnabled, onClickReverseCharge, onChangeLimit, onClickAssignInvoiceNumber,
        onDismissImportInfo, onCheckTodo, setupProgress, isImportingHistory, sendingHistory, tab, onCopyInvoiceID,
        reloadNumOpen, loadingUploadStats, uploadStats, onClickUpload, onClickUploadSettings, onUploadInvoice
    } = props;
    const filterRef = useRef<InvoiceFilterRef>(null);
    const bulkContainer = useRef<HTMLDivElement>(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const selectedLabel = allSelected ? `Alle facturen geselecteerd` : `${selectedInvoices.length} geselecteerd`;
    const shouldRenderTodos = useMemo(() => {
        const activeKey = context.accountContext.account?.getActiveAPIKey();
        const {pdf_layout, vat, mail, upload} = setupProgress;
        if (!activeKey) return !upload || !mail || !pdf_layout || !vat;
        else if (activeKey.activeSinceMoment?.isAtOrAfter(IDate.now().subtract(7, 'days')))
            return !mail || !pdf_layout || !vat || !upload;
        return false;
    }, [setupProgress, context.accountContext.account]);
    const rotateStats = useMemo(() => {
        return tab === DashboardTab.INVOICES && !shouldRenderTodos;
    }, [tab, shouldRenderTodos]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        for (const [key, value] of Object.entries(filter)) {
            if (key === 'customer_type') {
                if (value !== CustomerType.BOTH) count += 1;
            } else if (!!value) count += 1;
        }
        return count;
    }, [filter]);
    const filterLabel = !activeFilterCount ? null :
        activeFilterCount === 1 ? '1 actieve filter' : `${activeFilterCount} actieve filters`;

    function onResetFilter() {
        filterRef.current?.onReset();
    }

    function onCloseBulk() {
        if (bulkOpen && (!hasHover(bulkContainer.current)))
            setBulkOpen(false);
    }

    function onClickBulk() {
        setBulkOpen(!bulkOpen);
    }

    function addBulkListener() {
        document.addEventListener('click', onCloseBulk);
    }

    function removeBulkListener() {
        document.removeEventListener('click', onCloseBulk);
    }

    function getInvoiceOptionsMenu(invoice: Invoice): OptionsMenuItem[] {
        const invoiceOptions = getInvoiceOptions(invoice, context.userContext.user).filter((option: InvoiceOption) =>
            userHasRight(context, option.accessKey, invoice.accountId));
        return invoiceOptions.map((option: InvoiceOption) => {
            let onClick: MouseEventHandler = voidFunction;
            const optionType = option.type;
            if (optionType === InvoiceOptionName.CREDIT) onClick = onClickCredit(invoice);
            else if (optionType === InvoiceOptionName.DETAILS) onClick = onClickDetails(invoice);
            else if (optionType === InvoiceOptionName.DOWNLOAD) onClick = onClickDownload(invoice);
            else if (optionType === InvoiceOptionName.EDIT) onClick = onClickEdit(invoice);
            else if (optionType === InvoiceOptionName.PREVIEW) onClick = onPreviewFile(invoice);
            else if (optionType === InvoiceOptionName.SEND_INVOICE) onClick = onClickMail(invoice);
            else if (optionType === InvoiceOptionName.UPLOAD_INVOICE) onClick = onUploadInvoice(invoice);
            else if (optionType === InvoiceOptionName.REVERSE_CHARGE) onClick = onClickReverseCharge(invoice);
            else if (optionType === InvoiceOptionName.SET_NUMBER) onClick = onClickAssignInvoiceNumber(invoice);
            else if (optionType === InvoiceOptionName.CANCEL_EMAILS) onClick = onDisableLambda(invoice);
            else if (optionType === InvoiceOptionName.COPY_ID) onClick = onCopyInvoiceID(invoice);
            return {
                onClick, icon: option.icon, label: option.label, disabled: option.disabled,
            };
        })
    }

    function renderTodos() {
        const {pdf_layout, upload, vat, mail} = setupProgress;
        return (
            <div className='todos'>
                <Todo onClick={onClickInvoice} icon={fileTextFilled} titleText='Factuur ontwerpen'
                      infoText='BEKIJK EN PAS EVENTUEEL AAN' onCheckTodo={onCheckTodo('pdf_layout')}
                      toggled={pdf_layout} accessKey={AccessKey.PAGE_INVOICE_LAYOUT} keepClickable/>
                <Todo onClick={onClickVatSettings} icon={currencyEuro} titleText='BTW % producten'
                      infoText='OPTIONEEL VAN 21% AFWIJKEN' iconPrefix='fat' onCheckTodo={onCheckTodo('vat')}
                      toggled={vat} accessKey={AccessKey.PAGE_SETTINGS_PRODUCTS} keepClickable/>
                <Todo onClick={onClickMailSettings} icon={envelopeFill} titleText='Mails instellen'
                      infoText='OM AUTOMATISCH TE MAILEN' onCheckTodo={onCheckTodo('mail')} toggled={mail}
                      accessKey={AccessKey.PAGE_EMAIL} keepClickable/>
                <Todo onClick={onClickUploadSettings} onCheckTodo={onCheckTodo('upload')} icon='upload'
                      titleText='Auto uploaden' infoText='Upload facturen naar Bol' toggled={upload}
                      accessKey={AccessKey.PAGE_INVOICE_REQUESTS} keepClickable/>
            </div>
        )
    }

    function renderInvoicesContainer() {
        const isAdmin = context.userContext.user?.isSuperAdmin() || hasAdminLogin();
        return (
            <div className='invoices-column'>
                <TrialEndController/>
                {tab === DashboardTab.INVOICE_REQUESTS &&
                    <InvoiceRequestController reloadNumOpen={reloadNumOpen} stats={uploadStats}/>}
                {tab === DashboardTab.INVOICES && (
                    <>
                        {renderImportOverlay()}
                        <div className='filter-container'>
                            <label className='col-title'>Orders</label>
                            <div className='search-container'>
                                {renderLeftSearchContainer()}
                                {renderRightSearchContainer()}
                            </div>
                            {isAdmin && <div className='reload-btn' onClick={onReload}>
                                <AwesomeIcon name='rotate'/>
                            </div>}
                        </div>
                        {renderInvoices()}
                        <div className='invoices-footer'>
                            <div className='right'>
                                {renderCount()}
                                {renderPager('prev')}
                                {renderPager('next')}
                                <BolmateSelect onChange={onChangeLimit} value={limit} items={limitItems}
                                               itemsPosition='top'/>
                            </div>
                        </div>
                    </>
                )}
            </div>
        )
    }

    function renderImportOverlay() {
        if (!isImportingHistory) return null;
        return (
            <div className='import-overlay'>
                <DotsLoader dotSize='4rem'/>
                <div className='texts'>
                    <label className='import-title'>De orders van de afgelopen 30 dagen worden
                        geïmporteerd</label>
                    <label className='import-label'>We sturen je een e-mail zodra
                        het importeren voltooid is.<br/>Je kunt ondertussen jouw account naar wens instellen.</label>
                </div>
            </div>
        )
    }

    function renderInvoices() {
        const someSelected = selectedInvoices.length > 0;
        const checkIcon = <AwesomeIcon name={allSelected ? 'check' : 'minus'}/>;
        const checkColor = (allSelected || someSelected) ? COLOR_BLUE_BOLMATE : COLOR_GRAY_8;
        return (
            <div className='invoices'>
                <Loader active={loadingInvoices}/>
                <div className='row head'>
                    <Checkbox checked={allSelected || someSelected} onCheck={onSelectAll} checkIcon={checkIcon}
                              contrastColor={checkColor}/>
                    <label>ORDERNUMMER</label>
                    <label>FACTUURNUMMER</label>
                    <label>NAAM</label>
                    <label>TYPE</label>
                    <label>DATUM</label>
                    <label>BEDRAG</label>
                    <label>STATUS</label>
                    <label className='invoice-options'>ACTIES</label>
                </div>
                <ImportStatus onDismissImportInfo={onDismissImportInfo}/>
                {renderInvoiceRows()}
            </div>
        )
    }

    function renderInvoiceRows() {
        const {account} = context.accountContext, status = account?.status;
        if (invoices.length) return invoices.map(renderInvoice);
        else if (activeFilterCount) return renderInvoicesNotFound();
        else if (status === AccountStatus.IMPORTING) {
            if (isImportingHistory) return null;
            return renderImportingStatus();
        } else if (status === AccountStatus.ERROR || status === AccountStatus.SETUP_API) return renderConnectionError();
        else return renderEmptyInvoices();
    }

    function renderEmptyInvoices() {
        return (
            <div className='invoice-image-container empty'>
                <Image name='empty-dashboard' alt='Bolmate' className='invoice-image'/>
                {/*<img src={getImg('empty-dashboard')} className='invoice-image' alt='Bolmate.nl'/>*/}
                <label className='image-label'>Nog geen orders ontvangen</label>
                <label className='image-sub-label'>Zodra we de eerste order van Bol ontvangen wordt deze hier
                    getoond. Je kunt via de zoekbalk ook oudere orders opzoeken, dit kan tot 2 jaar terug!</label>
            </div>
        )
    }

    function renderConnectionError() {
        return (
            <div className='invoice-image-container connection-error'>
                <label className='image-sub-label'>Welkom in de Bolmate app!</label>
                <label className='image-sub-label'>In dit scherm zie je straks je opgehaalde orders.</label>
                <label className='image-sub-label'>Verbind Bolmate met Bol om jouw orders automatisch te kunnen
                    ophalen.</label>
                <label className='image-sub-label'>We versturen nog niks.</label>
                <Image name='connect-api' alt='Bolmate' className='invoice-image'/>
                <BolmateButton onClick={onClickApiFlow} label='Koppelen met Bol'
                               ornaments={{element: <AwesomeIcon name='link'/>}}/>
            </div>
        )
    }

    function renderInvoicesNotFound() {
        return (
            <div className='invoice-image-container not-found'>
                <Image name='data-rows' className='invoice-image'/>
                <label className='image-label'>Geen orders gevonden</label>
                <label className='image-sub-label'>Tip: voor zoeken op achternaam, bedrijf, factuurnummer etc.
                    Gebruik <Link onClick={onOpenFilters}>Uitgebreid zoeken</Link>.</label>
            </div>
        )
    }

    function renderImportingStatus() {
        return (
            <div className='importing-container'>
                <Loader active disableBackground position='relative' className='importing-loader'/>
                <div className='labels'>
                    <label className='main'>Orders aan het ophalen</label>
                    <label>De eerste keer kan dit enkele minuten duren.</label>
                    <label>Check de status rechts bovenin de pagina.</label>
                </div>
            </div>
        )
    }

    function getAccountName(accountId: string): string | null {
        for (const account of context.accountContext.accounts) {
            if (account.id === accountId) return account.selectablePlatformLabel;
        }
        return null;
    }

    function renderInvoice(invoice: Invoice) {
        const isSelected = allSelected || selectedInvoices.includes(invoice.id);
        const contrastColor = isSelected ? COLOR_BLUE_BOLMATE : COLOR_GRAY_8;
        const fromActiveAccount = activeAccountId === invoice.accountId;
        const accountName = fromActiveAccount ? null : getAccountName(invoice.accountId);
        const renderFromOtherAccount = getSetting('renderInvFromOtherAccount');
        let optionsMenu: OptionsMenu;

        function onClickRow() {
            optionsMenu.onOpenExternal();
        }

        return (
            <div key={invoice.id} className='row' onClick={onClickRow}>
                <Checkbox checked={isSelected} onCheck={onSelectInvoice(invoice.id)} contrastColor={contrastColor}/>
                <div className='order-id with-copy-action'>
                    {(!fromActiveAccount && renderFromOtherAccount) &&
                        renderInvoiceOtherAccount(accountName)}
                    <label className='bold ref'>{invoice.orderId}</label>
                    <CopyIcon onClick={onCopy(invoice.orderId, true)} className='copy-action' autoReset/>
                </div>
                <div className='with-copy-action'>
                    <label>{invoice.invoiceNum}</label>
                    <CopyIcon onClick={onCopy(invoice.invoiceNum, true)} className='copy-action' autoReset/>
                </div>
                <div className='with-copy-action'>
                    <label><CountryFlag countryCode={getCountryCode(invoice.country)}/> {invoice.fullName}</label>
                    <CopyIcon onClick={onCopy(invoice.fullName, true)} className='copy-action' autoReset/>
                </div>
                <label>{invoice.translatedCustomerType}</label>
                <label>{formatSimpleDate(invoice.momentDate)}</label>
                <label>&euro; {parseMoney(invoice.total)}</label>
                {renderInvoiceStatuses(invoice)}
                <OptionsMenu options={getInvoiceOptionsMenu(invoice)} placement={MenuPlacement.BOTTOM_LEFT}
                             optionsComponent={<Icon icon={optionsHorizontal} className='options-icon'/>}
                             containerClass='common-options' ref={(ref: OptionsMenu) => {
                    optionsMenu = ref
                }}/>
            </div>
        )
    }

    function renderInvoiceOtherAccount(accountName: string | null) {
        return (
            <div className='other-account-container'>
                <InfoIcon className='other-account-icon'/>
                <span className='other-account-message'>Deze order komt uit niet-geselecteerd
                    account {accountName}</span>
            </div>
        )
    }

    function renderInvoiceStatuses(invoice: Invoice) {
        const statuses: InvoiceStatusElement[] = getInvoiceStatusLabels(invoice);
        return (
            <div className='statuses' style={{gridTemplateColumns: `repeat(${statuses.length}, fit-content(100%))`}}>
                {statuses.map((element: InvoiceStatusElement, index: number) => {
                    let renderElement: React.ReactElement;
                    if (element.type === 'invoice-num') renderElement = React.cloneElement<any>(element.element,
                        {onClick: onClickAssignInvoiceNumber(invoice)});
                    else renderElement = element.element;
                    return <Fragment key={index}>{renderElement}</Fragment>
                })}
            </div>
        )
    }

    function renderLeftSearchContainer() {
        return (
            <div className='left'>
                <BolmateInput onChange={onChangeSearch} value={filter.order_id ?? ''} onEnter={onSearch}
                              label='Ordernummer' allowCharReg={/[A-Za-z0-9]/}
                              ornaments={{element: <AwesomeIcon name='search' className='search-icon'/>}}
                              className='search-field' inputProps={{placeholder: 'Zoek op ordernummer'}}/>
                <div className='filters-container'>
                    <BolmateButton onClick={onOpenFilters} label='Uitgebreid zoeken' secondary/>
                    {renderActiveFilters()}
                    <InvoiceFilterController onSubmit={onSubmitFilters} onClose={onCloseFilters}
                                             active={filtersOpen} filter={filter} ref={filterRef}/>
                </div>
                {renderSendingHistory()}
            </div>
        )
    }

    function renderActiveFilters() {
        if (!filterLabel) return null;
        return (
            <div className='active-filters-label'>
                <label className='filter-count' onClick={onOpenFilters}>{filterLabel}</label> - <span
                onClick={onResetFilter} className='delete-filters-btn'>Verwijder x</span>
            </div>
        )
    }

    function renderRightSearchContainer() {
        const bulkEnabled = selectedInvoices.length || allSelected,
            isSuperAdmin = context.userContext.user?.isSuperAdmin() || hasAdminLogin();
        return (
            <div className='right'>
                <div ref={bulkContainer}
                     className={classNames('bulk-actions', bulkEnabled && 'enabled', bulkOpen && 'open')}>
                    <label className='bulk-count-label'>{selectedLabel}</label>
                    <BolmateButton onClick={onClickBulk} label='Bulk acties' ornaments={{
                        element: <AwesomeIcon name='chevron-down' className='bulk-arrow'/>,
                        placement: ElementPosition.RIGHT
                    }} disabled={!bulkEnabled || !canBulkDownload} className='bulk-button'/>
                    <div className='bulk-actions-list'>
                        {canBulkDownload &&
                            <div className='bulk-action' onClick={onClickDownload(null)}>
                                <label className='bulk-action-label'>Facturen downloaden</label>
                            </div>}
                        {isSuperAdmin && (
                            <div className='bulk-action' onClick={onBulkApplyEmailSequence}>
                                <label className='bulk-action-label'>E-mails</label>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    function renderSendingHistory() {
        if (!sendingHistory) return null;
        return <div className='sending-history-label'>
            <AwesomeIcon name='paper-plane' className='plane-icon'/>
            <label>Bulk e-mails worden ingepland</label>
        </div>
    }

    function renderCount() {
        const start = (page - 1) * limit + 1;
        const end = page * limit;
        return <label className='count-label'>{start}-{end}</label>;
    }

    function renderPager(type: 'prev' | 'next') {
        let enabled: boolean;
        if (type === 'prev') enabled = page > 1;
        else enabled = invoices.length === limit;
        let icon: IconName, changePage: number, pagerType: PagerType;
        if (type === 'prev') {
            icon = 'chevron-left';
            changePage = page - 1;
            pagerType = PagerType.PREVIOUS;
        } else {
            icon = 'chevron-right';
            changePage = page + 1;
            pagerType = PagerType.NEXT;
        }
        return (
            <AwesomeIcon name={icon} className={classNames('pager-icon', enabled && 'enabled')}
                         onClick={enabled ? onChangePage(changePage, pagerType) : undefined}/>
        );
    }

    function renderNotificationsBanner() {
        const module = context.userContext.user?.subscription?.getModule(SubscriptionModuleName.NOTIFICATIONS);
        if (!module || module.status !== SubscriptionModuleStatus.TRIAL) return null;
        const daysRemaining = module.chargePerDate.diff(IDate.now(), 'days');
        return (
            <BasicMessage message={{id: '48228d9e-7f7e-4acb-80af-543c41da75d2', messageType: SystemMessageType.INFO}}
                          className='orders-notifications-banner'>
                <p>Je hebt nog {daysRemaining} {renderPlural(daysRemaining, 'dag', 'dagen')} proef op de Notificaties &
                    Review AI module.</p>
            </BasicMessage>
        );
    }

    useEffect(() => {
        if (bulkOpen) addBulkListener();
        else removeBulkListener();

        return function() {
            removeBulkListener();
        }
    }, [bulkOpen]);

    return (
        <>
            {/*{renderNotificationsBanner()}*/}
            <div className='dashboard'>
                <FlipCard flip={rotateStats} className='stats-flip'>
                    {shouldRenderTodos ? renderTodos() :
                        <UploadStatsController stats={uploadStats} loading={loadingUploadStats}/>}
                    <DashboardStats onClickApiFlow={onClickApiFlow} onClickMailSettings={onClickMailSettings}
                                    stats={stats} loading={loadingStats} emailsEnabled={emailsEnabled}
                                    onClickUpload={onClickUpload}/>
                </FlipCard>
                <MailMessage onClickMailSettings={onClickMailSettings} onClickApiFlow={onClickApiFlow}
                             doesAutoSend={emailsEnabled}/>
                {renderInvoicesContainer()}
            </div>
        </>
    )
}